import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues } from '../../../util/helper';
import { getLocationData, updateLocationData, getLocationAliasSuggestions } from '../../../services/Location';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";


const OtherLandmarks = () => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();


  const schema = {
    "title": "Other Landmarks fields",
    "description": "",
    "type": "object",
    "properties": {
      "other_landmarks": {
        "type": "array",
        "title": "Other Landmarks",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      },
    }
  }

  const fetchLocationAliasSuggestions = async (inputValue) => {
    const fetchedLocationAliasSuggestions = await loadLocationAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedLocationAliasSuggestions) {
      return [];
    }

    return fetchedLocationAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadLocationAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load location suggestions");
      return [];
    }
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchLocationAliasSuggestions} />
    ),
  };

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    other_landmarks: { 
      "ui:emptyValue": "",
      items: {
        alias: {
          "ui:widget": "typeahead"
        }
      }
    },
  };

  const formData = basicFieldData;

  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getLocationData(alias, 'introduction', 'other-landmarks');
          if (result) {
            setBasicFieldData(transFormData(result?.[0]));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  }, [alias, toast, quickSave]);

  const transFormData = (data) => ({
    ...data,
    certificate_image_alias: data?.certificate_image_alias?.[0],
  });

  const reTransFormData = (data) => ({
    ...data,
    certificate_image_alias: [data?.certificate_image_alias],
  });

  // Submit updated data
  const handleSubmit = async (formData) => {

    const originalFormattedData = reTransFormData(formData);
    const deletedValues = getDeletedValues(basicFieldData, originalFormattedData);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateLocationData(alias, originalFormattedData, deletedValues);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);

                    showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      console.error('Error updating film data:', error);
            showErrorToast(toast, "Failed to update film data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form schema={schema} uiSchema={uiSchema} formData={formData} validator={validator} widgets={widgets} onSubmit={(e) => handleSubmit(e.formData)} />
      )}
    </Box>
  );
};

export default OtherLandmarks;